import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, Lock, User, UserPlus, MapPin, Phone, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showGuest, setShowGuest] = useState(false);
  const { login, setUser } = useAuth();

  // Guest form state
  const [guestName, setGuestName] = useState('');
  const [guestContact, setGuestContact] = useState('');
  const [guestCity, setGuestCity] = useState('');
  const [guestState, setGuestState] = useState('');
  const [guestSource, setGuestSource] = useState('');
  const [guestLoading, setGuestLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error('Please enter username and password');
      return;
    }
    setLoading(true);
    try {
      await login(username, password);
      toast.success('Login successful');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGuestRegister = async (e) => {
    e.preventDefault();
    if (!guestName || !guestContact || !guestCity || !guestState) {
      toast.error('Please fill all required fields');
      return;
    }
    setGuestLoading(true);
    try {
      const res = await axios.post(`${API}/auth/guest-register`, {
        name: guestName, contact: guestContact, city: guestCity, state: guestState, source: guestSource
      });
      const { token, user } = res.data;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      if (setUser) setUser(user);
      toast.success('Welcome! You can now browse and place orders.');
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setGuestLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-emerald-100">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center space-y-2">
          <div className="flex justify-center mb-4">
            <img src="/purelife-icon-192.png" alt="Purelife" className="w-16 h-16 rounded-full object-cover" />
          </div>
          <CardTitle className="text-2xl font-bold">Purelife Bot</CardTitle>
          <CardDescription>B2B Sales Management System</CardDescription>
        </CardHeader>
        <CardContent>
          {!showGuest ? (
            <>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="username">Username</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input id="username" type="text" placeholder="Enter username" value={username}
                      onChange={(e) => setUsername(e.target.value)} className="pl-9" disabled={loading} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input id="password" type="password" placeholder="Enter password" value={password}
                      onChange={(e) => setPassword(e.target.value)} className="pl-9" disabled={loading} />
                  </div>
                </div>
                <Button type="submit" className="w-full" disabled={loading} data-testid="login-btn">
                  {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Logging in...</> : 'Login'}
                </Button>
              </form>
              <div className="mt-4 pt-4 border-t text-center">
                <p className="text-xs text-muted-foreground mb-2">New here?</p>
                <Button variant="outline" className="w-full gap-2" onClick={() => setShowGuest(true)} data-testid="guest-login-btn">
                  <UserPlus className="w-4 h-4" /> Guest Login
                </Button>
              </div>
            </>
          ) : (
            <>
              <button onClick={() => setShowGuest(false)} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-gray-700 mb-3">
                <ArrowLeft className="w-3 h-3" /> Back to Login
              </button>
              <form onSubmit={handleGuestRegister} className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Name *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input placeholder="Your name" value={guestName} onChange={e => setGuestName(e.target.value)}
                      className="pl-9 h-9" disabled={guestLoading} data-testid="guest-name" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Contact Number *</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input placeholder="Phone number" value={guestContact} onChange={e => setGuestContact(e.target.value)}
                      className="pl-9 h-9" disabled={guestLoading} data-testid="guest-contact" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1.5">
                    <Label className="text-xs">City *</Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input placeholder="City" value={guestCity} onChange={e => setGuestCity(e.target.value)}
                        className="pl-9 h-9" disabled={guestLoading} data-testid="guest-city" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">State *</Label>
                    <Input placeholder="State" value={guestState} onChange={e => setGuestState(e.target.value)}
                      className="h-9" disabled={guestLoading} data-testid="guest-state" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">How did you hear about us? (Optional)</Label>
                  <Input placeholder="e.g., Google, Referral, IndiaMart..." value={guestSource}
                    onChange={e => setGuestSource(e.target.value)} className="h-9" disabled={guestLoading} />
                </div>
                <Button type="submit" className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700" disabled={guestLoading} data-testid="guest-submit-btn">
                  {guestLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Registering...</> : <><UserPlus className="w-4 h-4" /> Continue as Guest</>}
                </Button>
              </form>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
